export class MimeType {
    extension: string | string[];
    description: string;
    icon: string;

    /**
     * Constructor de la clase `MimeType`.
     * @param {string} mimeType - El tipo de MIME a partir del cual se inicializa el objeto.
     */
    constructor(mimeType: string) {
        this.extension = "";
        this.description = mimeType;
        this.icon = "draft";
    }
}

/**
 * Constante que contiene todos los tipos de MIME definidos en la aplicación.
 * @constant {string}
 */

export const MIME_TYPE_JSON: string = "application/json";
export const MIME_TYPE_MSWORD: string = "application/msword";
export const MIME_TYPE_PDF: string = "application/pdf";
export const MIME_TYPE_EXCEL: string = "application/vnd.ms-excel";
export const MIME_TYPE_EXCEL_MACRO: string = "application/vnd.ms-excel.sheet.macroEnabled.12";
export const MIME_TYPE_POWERPOINT: string = "application/vnd.ms-powerpoint";
export const MIME_TYPE_POWERPOINT_MACRO: string = "application/vnd.ms-powerpoint.presentation.macroEnabled.12";
export const MIME_TYPE_WORD_MACRO: string = "application/vnd.ms-word.document.macroEnabled.12";
export const MIME_TYPE_POWERPOINT_XML: string = "application/vnd.openxmlformats-officedocument.presentationml.presentation";
export const MIME_TYPE_EXCEL_XML: string = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const MIME_TYPE_WORD_XML: string = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const MIME_TYPE_EXE: string = "application/x-msdownload";
export const MIME_TYPE_ZIP: string = "application/x-zip-compressed";
export const MIME_TYPE_CSV: string = "text/csv";
export const MIME_TYPE_MIDI: string = "audio/midi";
export const MIME_TYPE_MP3: string = "audio/mpeg";
export const MIME_TYPE_REAL_AUDIO: string = "audio/x-pn-realaudio";
export const MIME_TYPE_WAV: string = "audio/x-wav";
export const MIME_TYPE_APNG: string = "image/apng";
export const MIME_TYPE_BMP: string = "image/bmp";
export const MIME_TYPE_GIF: string = "image/gif";
export const MIME_TYPE_JPEG: string = "image/jpeg";
export const MIME_TYPE_PNG: string = "image/png";
export const MIME_TYPE_SVG: string = "image/svg+xml";
export const MIME_TYPE_TIFF: string = "image/tiff";
export const MIME_TYPE_WEBP: string = "image/webp";
export const MIME_TYPE_HTML: string = "text/html";
export const MIME_TYPE_PLAIN: string = "text/plain";
export const MIME_TYPE_XML: string = "text/xml";
export const MIME_TYPE_MPEG_VIDEO: string = "video/mpeg";

/**
 * Objeto que mapea los tipos de MIME a sus respectivas descripciones y íconos.
 * @constant {Object.<string, MimeType>}
 */
export const MIMETYPES: { [id: string]: MimeType } = {
    [MIME_TYPE_JSON]: {
        extension: ".json",
        description: "JSON",
        icon: "file_json"
    },
    [MIME_TYPE_MSWORD]: {
        extension: ".doc",
        description: "Microsoft Word (Versión antigua)",
        icon: "description"
    },
    [MIME_TYPE_PDF]: {
        extension: ".pdf",
        description: "Adobe Portable Document Format (PDF)",
        icon: "picture_as_pdf"
    },
    [MIME_TYPE_EXCEL]: {
        extension: ".xls",
        description: "Microsoft Excel",
        icon: "view_list"
    },
    [MIME_TYPE_EXCEL_MACRO]: {
        extension: ".xlsm",
        description: "Microsoft Excel Macro-Enabled Spreadsheet",
        icon: "view_list"
    },
    [MIME_TYPE_POWERPOINT]: {
        extension: ".ppt",
        description: "Microsoft PowerPoint",
        icon: "tab"
    },
    [MIME_TYPE_POWERPOINT_MACRO]: {
        extension: ".pptm",
        description: "Microsoft PowerPoint Macro-Enabled Presentation",
        icon: "tab"
    },
    [MIME_TYPE_WORD_MACRO]: {
        extension: ".docm",
        description: "Microsoft Word Macro-Enabled Document",
        icon: "description"
    },
    [MIME_TYPE_POWERPOINT_XML]: {
        extension: ".pptx",
        description: "Microsoft PowerPoint Open XML Presentation",
        icon: "tab"
    },
    [MIME_TYPE_EXCEL_XML]: {
        extension: ".xlsx",
        description: "Microsoft Excel Open XML Spreadsheet",
        icon: "view_list"
    },
    [MIME_TYPE_WORD_XML]: {
        extension: ".docx",
        description: "Microsoft Word Open XML Document",
        icon: "description"
    },
    [MIME_TYPE_EXE]: {
        extension: ".exe",
        description: "Archivo ejecutable",
        icon: "draft"
    },
    [MIME_TYPE_ZIP]: {
        extension: ".zip",
        description: "Archivo ZIP",
        icon: "folder_zip"
    },
    [MIME_TYPE_CSV]: {
        extension: ".csv",
        description: "Comma-Separated Values (CSV)",
        icon: "list"
    },
    [MIME_TYPE_MIDI]: {
        extension: [".mid", ".midi"],
        description: "Interfaz Digital de Instrumentos Musicales (MIDI)",
        icon: "audio_file"
    },
    [MIME_TYPE_MP3]: {
        extension: ".mp3",
        description: "Audio MPEG (MP3)",
        icon: "audio_file"
    },
    [MIME_TYPE_REAL_AUDIO]: {
        extension: ".ram",
        description: "RealAudio",
        icon: "audio_file"
    },
    [MIME_TYPE_WAV]: {
        extension: ".wav",
        description: "Formato de audio de forma de onda (WAV)",
        icon: "audio_file"
    },
    [MIME_TYPE_APNG]: {
        extension: ".apng",
        description: "Animated Portable Network Graphics (APNG) image",
        icon: "image"
    },
    [MIME_TYPE_BMP]: {
        extension: ".bmp",
        description: "Bitmap Image File (BMP)",
        icon: "image"
    },
    [MIME_TYPE_GIF]: {
        extension: ".gif",
        description: "Graphics Interchange Format (GIF)",
        icon: "gif"
    },
    [MIME_TYPE_JPEG]: {
        extension: [".jpeg", ".jpg", ".jpe"],
        description: "Joint Photographic Expert Group image (JPEG)",
        icon: "image"
    },
    [MIME_TYPE_PNG]: {
        extension: ".png",
        description: "Portable Network Graphics (PNG)",
        icon: "file_png"
    },
    [MIME_TYPE_SVG]: {
        extension: ".svg",
        description: "Scalable Vector Graphics (SVG)",
        icon: "image"
    },
    [MIME_TYPE_TIFF]: {
        extension: [".tif", ".tiff"],
        description: "Formato de archivo de imagen etiquetado (TIFF)",
        icon: "image"
    },
    [MIME_TYPE_WEBP]: {
        extension: ".webp",
        description: "Web Picture format (WEBP)",
        icon: "image"
    },
    [MIME_TYPE_HTML]: {
        extension: [".htm", ".html"],
        description: "Hipertexto (HTML)",
        icon: "html"
    },
    [MIME_TYPE_PLAIN]: {
        extension: ".txt",
        description: "Texto plano (TXT)",
        icon: "abc"
    },
    [MIME_TYPE_XML]: {
        extension: ".xml",
        description: "XML",
        icon: "draft"
    },
    [MIME_TYPE_MPEG_VIDEO]: {
        extension: ".mpeg",
        description: "Video MPEG",
        icon: "video_file"
    }
};